import { Typography, message } from "antd";
import { RcFile } from "antd/es/upload";
import { UploadFile } from "antd/es/upload/interface";
import React, { useState } from "react";

import {
  useGetPresignedUrls,
  useUploadFileToPresignedUrl,
} from "~/services/upload/upload.mutations";
import PhotoPreviewModal from "./photo-preview-modal";
import PhotoTypeSelection from "./photo-type-selection";
import PhotoUploadModal, { PhotoFile } from "./photo-upload-modal";
import RecentPhotos from "./recent-photos";
import { getBase64 } from "./utils";
import { useCreateImages } from "~/services/image";

const { Title } = Typography;

export const Photos: React.FC = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [fileList, setFileList] = useState<PhotoFile[]>([]);
  const [photoType, setPhotoType] = useState<"inventory" | "document" | null>(
    null
  );
  const [previewImage, setPreviewImage] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");

  const getPresignedMutation = useGetPresignedUrls();
  const uploadPresignedMutation = useUploadFileToPresignedUrl();
  const createImagesMutation = useCreateImages();

  const handleUploadChange = ({ fileList }: any) => {
    // Filter for only image files
    const filteredFileList = fileList.filter((file: any) => {
      const isImage = file.type?.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
      }
      return isImage;
    });

    setFileList(filteredFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  const handleNewPhoto = (type: "inventory" | "document") => {
    setPhotoType(type);
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
    setFileList([]);
    setPhotoType(null);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error("Please select at least one photo to upload");
      return;
    }

    setIsUploading(true);

    try {
      const fileParams = fileList.map((file) => ({
        fileName: file.name,
        mimeType: file.type,
      }));

      const presignedUrls = await getPresignedMutation.mutateAsync(fileParams);
      const uploadedUrls: string[] = [];

      for (const [key, url] of presignedUrls.entries()) {
        const file = fileList.find((_, idx) => idx === key)?.originFileObj;

        if (!file) continue;

        await uploadPresignedMutation.mutateAsync({
          presignedUrl: url.presignedUrl,
          file,
        });

        // Store the s3 URL for later use
        uploadedUrls.push(url.url);
      }

      // Create image records in the database with the uploaded URLs
      if (uploadedUrls.length > 0) {
        const images = uploadedUrls.map((url, index) => {
          const file = fileList[index];
          return {
            url,
            name: file.name,
            size: file.size?.toString(),
          };
        });

        await createImagesMutation.mutateAsync({ images });
      }

      message.success("Photos uploaded successfully!");
      setVisible(false);
      setFileList([]);
      setPhotoType(null);
    } catch (error) {
      console.error("Error uploading photos:", error);
      message.error("Failed to upload photos. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreviewPhoto = (imageUrl: string, title: string) => {
    setPreviewImage(imageUrl);
    setPreviewTitle(title);
    setPreviewVisible(true);
  };

  return (
    <div className="space-y-3 md:space-y-4 md:space-y-4">
      <Title level={3} className="mb-0 text-lg md:text-xl ">
        Photos
      </Title>

      <PhotoTypeSelection onSelectType={handleNewPhoto} />

      <RecentPhotos onPreviewPhoto={handlePreviewPhoto} />

      <PhotoUploadModal
        visible={visible}
        photoType={photoType}
        fileList={fileList}
        isUploading={isUploading}
        onCancel={handleCancel}
        onUpload={handleUpload}
        onUploadChange={handleUploadChange}
        onPreview={handlePreview}
      />

      <PhotoPreviewModal
        visible={previewVisible}
        title={previewTitle}
        imageUrl={previewImage}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  );
};
